import { produce } from 'immer';

import { RESET_STATE } from 'store/global/actions';

import { GUIDELINES_ACTION_TYPE } from '../actions';

const initialState = {
  sources: [],
  versions: {
    bySourceId: {},
    byId: {},
    selectedBySourceId: {},
  },
  sections: {
    tocByVersionId: {},
    byId: {},
    selectedByVersionId: {},
  },
  chunks: {
    bySectionId: {},
  },
};

const replaceById = (list, item) =>
  list.some(({ id }) => id === item.id)
    ? list.map(entry => (entry.id === item.id ? item : entry))
    : [...list, item];

const guidelinesReducer = (state = initialState, { type, payload }) =>
  produce(state, draft => {
    switch (type) {
      case GUIDELINES_ACTION_TYPE.GET_SOURCES:
        draft.sources = payload;
        break;

      case GUIDELINES_ACTION_TYPE.GET_SOURCE_BY_ID:
        draft.sources = replaceById(state.sources, payload);
        break;

      case GUIDELINES_ACTION_TYPE.CREATE_SOURCE:
        draft.sources = [...state.sources, payload];
        break;

      case GUIDELINES_ACTION_TYPE.UPDATE_SOURCE:
        draft.sources = state.sources.map(source => (source.id === payload.id ? payload : source));
        break;

      case GUIDELINES_ACTION_TYPE.DELETE_SOURCE:
        draft.sources = state.sources.filter(({ id }) => id !== payload);
        break;

      case GUIDELINES_ACTION_TYPE.GET_VERSIONS_BY_SOURCE:
        draft.versions.bySourceId[payload.sourceId] = payload.versions;
        payload.versions.forEach(version => {
          draft.versions.byId[version.id] = version;
        });
        break;

      case GUIDELINES_ACTION_TYPE.CREATE_VERSION: {
        const { sourceId } = payload;
        const existing = state.versions.bySourceId[sourceId] ?? [];
        draft.versions.bySourceId[sourceId] = replaceById(existing, payload);
        draft.versions.byId[payload.id] = payload;
        break;
      }

      case GUIDELINES_ACTION_TYPE.DELETE_VERSION: {
        const existing = state.versions.bySourceId[payload.sourceId] ?? [];
        draft.versions.bySourceId[payload.sourceId] = existing.filter(
          ({ id }) => id !== payload.id,
        );
        delete draft.versions.byId[payload.id];

        if (state.versions.selectedBySourceId[payload.sourceId] === payload.id) {
          delete draft.versions.selectedBySourceId[payload.sourceId];
        }

        break;
      }

      case GUIDELINES_ACTION_TYPE.SET_SELECTED_VERSION_ID:
        draft.versions.selectedBySourceId[payload.sourceId] = payload.versionId;
        break;

      case GUIDELINES_ACTION_TYPE.GET_VERSION_TOC:
        draft.sections.tocByVersionId[payload.versionId] = payload.sections;
        break;

      case GUIDELINES_ACTION_TYPE.GET_SECTION_BY_ID:
        draft.sections.byId[payload.id] = {
          ...state.sections.byId[payload.id],
          ...payload,
        };
        break;

      case GUIDELINES_ACTION_TYPE.CREATE_SECTION: {
        const { versionId } = payload;
        const existingToc = state.sections.tocByVersionId[versionId];
        draft.sections.byId[payload.id] = payload;

        if (existingToc) {
          draft.sections.tocByVersionId[versionId] = [...existingToc, payload];
        }

        break;
      }

      case GUIDELINES_ACTION_TYPE.UPDATE_SECTION: {
        const { versionId } = payload;
        draft.sections.byId[payload.id] = payload;

        const existingToc = state.sections.tocByVersionId[versionId];

        if (existingToc) {
          draft.sections.tocByVersionId[versionId] = replaceById(existingToc, payload);
        }

        break;
      }

      case GUIDELINES_ACTION_TYPE.DELETE_SECTION: {
        const existingToc = state.sections.tocByVersionId[payload.versionId];

        if (existingToc) {
          draft.sections.tocByVersionId[payload.versionId] = existingToc.filter(
            ({ id }) => id !== payload.id,
          );
        }

        delete draft.sections.byId[payload.id];
        delete draft.chunks.bySectionId[payload.id];

        if (state.sections.selectedByVersionId[payload.versionId] === payload.id) {
          delete draft.sections.selectedByVersionId[payload.versionId];
        }

        break;
      }

      case GUIDELINES_ACTION_TYPE.SET_SELECTED_SECTION_ID:
        draft.sections.selectedByVersionId[payload.versionId] = payload.sectionId;
        break;

      case GUIDELINES_ACTION_TYPE.GET_CHUNKS_BY_SECTION:
        draft.chunks.bySectionId[payload.sectionId] = payload.chunks;
        break;

      case GUIDELINES_ACTION_TYPE.REGENERATE_CHUNK_EMBEDDING: {
        const existingChunks = state.chunks.bySectionId[payload.sectionId] ?? [];
        draft.chunks.bySectionId[payload.sectionId] = replaceById(existingChunks, payload.chunk);
        break;
      }

      case GUIDELINES_ACTION_TYPE.TOGGLE_SOURCE_EXCLUSION: {
        const { sourceId, isExcluded } = payload;
        const sourceIndex = state.sources.findIndex(({ id }) => id === sourceId);

        if (sourceIndex >= 0) {
          draft.sources[sourceIndex].isExcluded = isExcluded;
        }

        break;
      }

      case GUIDELINES_ACTION_TYPE.RESET_GUIDELINES_STATE:
      case RESET_STATE:
        return initialState;

      default:
        break;
    }
  });

export default guidelinesReducer;
